"use client";
import React, { useContext, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
  DialogTitle,
} from "@/components/ui/dialog";
import { Typography } from "@material-tailwind/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useSendOtpMutation,
  useVerifyOtpMutation,
} from "@/redux/slices/authSlice";
import myContext from "@/context/myContext";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Lock, MessageCircle, Smartphone } from "lucide-react";
import { Loader2 } from "lucide-react";

export function LoginModal({ open, onOpenChange }) {
  
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");

  const [sendOtp, { isLoading: isSendingOtp }] = useSendOtpMutation();
  const [verifyOtp, { isLoading: isVerifyingOtp }] = useVerifyOtpMutation();

  const { showAlert, updateAuthState } = useContext(myContext);

  const handleOpen = () => {
    setOpen((prev) => !prev);
    if (!open) {
      setIsOtpSent(false);
      setMobileNumber("");
      setOtp("");
    }
  };

  const handleSendOtp = async () => {
    try {
      await sendOtp(mobileNumber).unwrap();
      setIsOtpSent(true);
      showAlert("OTP sent successfully!", "success");
    } catch (error) {
      showAlert(error?.data?.error || "Failed to send OTP", "error");
    }
  };

  // Add useEffect to reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setIsOtpSent(false);
      setMobileNumber("");
      setOtp("");
    }
  }, [open]);

  // Update handleVerifyOtp to close modal on success
  const handleVerifyOtp = async () => {
    try {
      await verifyOtp({ mobileNumber, otp }).unwrap();
      updateAuthState();
      showAlert("Login successful!", "success");
      onOpenChange(false); // Close modal on successful login
    } catch (error) {
      showAlert(error?.data?.error || "Invalid OTP", "error");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
      <Button variant="default" className="shadow-lg cursor-pointer">
          Sign In
        </Button>
      </DialogTrigger>

      <DialogContent className="p-0 overflow-hidden border-0 rounded-2xl sm:max-w-[425px]">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative"
        >
          <div className="bg-gradient-to-br from-indigo-600 to-blue-500 px-8 pt-10 pb-6">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-white">
                {isOtpSent ? "Verify Your Account" : "Welcome Back"}
              </DialogTitle>
            </DialogHeader>
            <Typography className="mt-2 text-sm font-medium text-indigo-100">
              {isOtpSent
                ? "Enter the 4-digit code sent to your WhatsApp"
                : "Sign in to continue to our services"}
            </Typography>
          </div>

          <div className="px-8 py-6 space-y-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={isOtpSent ? "otp" : "mobile"}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {!isOtpSent ? (
                  <div className="space-y-6">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Smartphone className="w-5 h-5 text-gray-400" />
                      </div>
                      <Input
                        type="tel"
                        placeholder="Enter mobile number"
                        value={mobileNumber}
                        onChange={(e) => setMobileNumber(e.target.value)}
                        className="pl-10"
                        maxLength={10}
                      />
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-blue-50 text-blue-800">
                      <MessageCircle className="w-4 h-4" />
                      <span>OTP will be sent via WhatsApp</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Lock className="w-5 h-5 text-gray-400" />
                      </div>
                      <Input
                        type="number"
                        placeholder="Enter 4-digit OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        className="pl-10"
                        maxLength={4}
                      />
                    </div>
                    <div className="text-center">
                      <button
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                        onClick={() => setIsOtpSent(false)}
                      >
                        Change mobile number
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="space-y-4"
            >
              {!isOtpSent ? (
                <Button
                  onClick={handleSendOtp}
                  disabled={!mobileNumber || mobileNumber.length !== 10 || isSendingOtp}
                  className="w-full h-12 font-semibold text-white transition-all duration-300 bg-indigo-600 rounded-xl hover:bg-indigo-700"
                >
                  {isSendingOtp ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    "Send OTP via WhatsApp"
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleVerifyOtp}
                  disabled={!otp || otp.length !== 4 || isVerifyingOtp}
                  className="w-full h-12 font-semibold text-white transition-all duration-300 bg-green-600 rounded-xl hover:bg-green-700"
                >
                  {isVerifyingOtp ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify & Continue"
                  )}
                </Button>
              )}
            </motion.div>

            <p className="text-xs text-center text-gray-500">
              By continuing, you agree to our{" "}
              <a href="#" className="font-medium text-indigo-600 hover:underline">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="#" className="font-medium text-indigo-600 hover:underline">
                Privacy Policy
              </a>
            </p>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}